import BrandLabel from "./BrandLabel.jsx";
import { getBrandColor } from "./brand-presentation.js";
import "./card-price-edit.css";
import ProductDetails, { PriceEditor, EditIcon } from "./ProductDetails.jsx";
import { getDefaultLaunchDates } from "./default-filters.js";
import FilterPanel from "./FilterPanel.jsx";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import {
    fetchBrands,
    fetchCountries,
    fetchProductFilterOptions,
    updateProductPrice,
} from "./api.js";
import { useRemoteProducts } from "./use-remote-products.js";
import {
    DEFAULT_RANGE,
    createBuckets,
    formatConfigurations,
    getProductConfigurations,
    formatPrice,
    isInBucket,
    moveBrand,
    validateLaunchDates,
    validateRange,
} from "./matrix.js";
import {
    CARD_FIELD_OPTIONS,
    DEFAULT_CARD_FIELDS,
    readCardPreferences,
    saveCardPreferences,
} from "./card-fields.js";
import "./styles.css";

function Icon({ name, size = 18 }) {
    const paths = {
        search: (
            <>
                <circle cx="10.5" cy="10.5" r="6.5" />
                <path d="m16 16 4 4" />
            </>
        ),
        filter: (
            <>
                <path d="M4 6h16M7 12h10M10 18h4" />
                <circle cx="8" cy="6" r="2" />
                <circle cx="15" cy="12" r="2" />
            </>
        ),
        grip: (
            <>
                <circle cx="9" cy="5" r="1" />
                <circle cx="15" cy="5" r="1" />
                <circle cx="9" cy="12" r="1" />
                <circle cx="15" cy="12" r="1" />
                <circle cx="9" cy="19" r="1" />
                <circle cx="15" cy="19" r="1" />
            </>
        ),
        arrow: <path d="m9 5 7 7-7 7" />,
        close: <path d="m6 6 12 12M18 6 6 18" />,
        refresh: (
            <>
                <path d="M20 7v5h-5M4 17v-5h5" />
                <path d="M6 7a7 7 0 0 1 12-1l2 3M4 15l2 3a7 7 0 0 0 12-1" />
            </>
        ),
    };
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            {paths[name]}
        </svg>
    );
}

function App() {
    const [brands, setBrands] = useState([]);
    const [countries, setCountries] = useState([]);
    const [filterOptions, setFilterOptions] = useState({
        ram: [],
        storage: [],
        network: [],
        groupOfRamRom: []
    });
    const [lookupsReady, setLookupsReady] = useState(false);
    const [lookupsLoading, setLookupsLoading] = useState(true);
    const [lookupsError, setLookupsError] = useState("");
    const [lookupRevision, setLookupRevision] = useState(0);
    const [productRevision, setProductRevision] = useState(0);
    const [order, setOrder] = useState([]);
    const [filters, setFilters] = useState(() => ({
        query: "",
        brandIds: [],
        ram: "",
        storage: "",
        network: "",
        countryId: "",
        groupOfRamRom: "",
        ...getDefaultLaunchDates(),
    }));
    const [fieldPanel, setFieldPanel] = useState(false);
    const [initialCardPreferences] = useState(() => {
        try {
            return readCardPreferences(window.localStorage);
        } catch {
            return { fields: { ...DEFAULT_CARD_FIELDS }, remember: true };
        }
    });
    const [cardFields, setCardFields] = useState(initialCardPreferences.fields);
    const [rememberFields, setRememberFields] = useState(
        initialCardPreferences.remember,
    );
    const [storageError, setStorageError] = useState(false);
    useEffect(() => {
        try {
            setStorageError(
                !saveCardPreferences(window.localStorage, cardFields, rememberFields),
            );
        } catch {
            setStorageError(true);
        }
    }, [cardFields, rememberFields]);
    const [range, setRange] = useState(DEFAULT_RANGE);
    const [draft, setDraft] = useState({ min: "100", max: "500", step: "50" });
    const [rangeError, setRangeError] = useState("");
    const [detail, setDetail] = useState(null);
    const [priceEditor, setPriceEditor] = useState(null);
    const [drag, setDrag] = useState(null);
    const dragRef = useRef(null);
    const [announcement, setAnnouncement] = useState("");

    useEffect(() => {
        const controller = new AbortController();
        setLookupsReady(false);
        setLookupsLoading(true);
        setLookupsError("");
        // Each dictionary has its own request and state. Products are requested only
        // once all dictionary requests finish; no dictionary is inferred from rows.
        const brandsRequest = fetchBrands({ signal: controller.signal }).then(
            (rows) => {
                if (controller.signal.aborted) return;
                if (!Array.isArray(rows)) throw new Error("品牌接口格式不正确");
                setBrands(rows);
                setOrder(rows.map((brand) => brand.id));
                setFilters((prev) => ({
                    ...prev,
                    brandIds: rows.map((brand) => brand.id),
                }));
            },
        );
        const countriesRequest = fetchCountries({ signal: controller.signal }).then(
            (rows) => {
                if (controller.signal.aborted) return;
                if (!Array.isArray(rows)) throw new Error("国家接口格式不正确");
                setCountries(rows);
                setFilters((prev) => ({
                    ...prev,
                    countryId:
                        rows.find((country) => country.code === "global")?.id || "",
                }));
            },
        );
        const optionsRequest = fetchProductFilterOptions({
            signal: controller.signal,
        }).then((options) => {
            if (controller.signal.aborted) return;
            if (
                !Array.isArray(options.ram) ||
                !Array.isArray(options.storage) ||
                !Array.isArray(options.network)
            )
                throw new Error("筛选选项接口格式不正确");
            setFilterOptions(options);
        });
        Promise.all([brandsRequest, countriesRequest, optionsRequest])
            .then(() => {
                if (!controller.signal.aborted) setLookupsReady(true);
            })
            .catch((error) => {
                if (!controller.signal.aborted)
                    setLookupsError(error.message || "筛选字典加载失败");
            })
            .finally(() => {
                if (!controller.signal.aborted) setLookupsLoading(false);
            });
        return () => controller.abort();
    }, [lookupRevision]);

    const [debouncedSearch, setDebouncedSearch] = useState("");
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(filters.query), 250);
        return () => clearTimeout(timer);
    }, [filters.query]);
    const launchError = validateLaunchDates(
        filters.launchStart,
        filters.launchEnd,
    );
    const productQuery = useMemo(
        () => ({
            query: debouncedSearch,
            brandIds: filters.brandIds,
            countryId: filters.countryId,
            ram: filters.ram,
            storage: filters.storage,
            network: filters.network,
            launchStart: filters.launchStart,
            launchEnd: filters.launchEnd,
            groupOfRamRom: filters.groupOfRamRom
            // minPrice: range.min,
            // maxPrice: range.max,
        }),
        [
            debouncedSearch,
            filters.brandIds,
            filters.countryId,
            filters.ram,
            filters.storage,
            filters.network,
            filters.launchStart,
            filters.launchEnd,
            filters.groupOfRamRom
            // range.min,
            // range.max,
        ],
    );
    const remote = useRemoteProducts(
        productQuery,
        lookupsReady && !launchError,
        productRevision,
    );
    const products = remote.products;
    const maxPrice = remote.maxPrice;
    const minPrice = remote.minPrice;
    const noProducts = remote.noPriceCount;
    const loading =
        lookupsLoading || remote.loading || filters.query !== debouncedSearch;
    const loadError = lookupsError || remote.error || launchError;
    function retryLoad() {
        if (!lookupsReady || lookupsError) setLookupRevision((value) => value + 1);
        else setProductRevision((value) => value + 1);
    }

    useEffect(() => {
        const cancel = (e) => {
            if (e.key === "Escape") {
                dragRef.current = null;
                setDrag(null);
                setFieldPanel(false);
            }
        };
        window.addEventListener("keydown", cancel);
        return () => window.removeEventListener("keydown", cancel);
    }, []);

    const brandMap = useMemo(
        () => new Map(brands.map((b) => [b.id, b])),
        [brands],
    );
    const visibleBrands = order
        .filter((id) => filters.brandIds.includes(id))
        .map((id) => brandMap.get(id))
        .filter(Boolean);
    const buckets = useMemo(() => createBuckets(range), [range]);
    const countryMap = useMemo(
        () => new Map(countries.map((country) => [country.id, country])),
        [countries],
    );
    // Join labels by IDs for display only; these are not product filter conditions.
    const productRows = useMemo(
        () =>
            products.map((product) => ({
                ...product,
                country: countryMap.get(product.countryId)?.name || "未指定",
            })),
        [products, countryMap],
    );
    // Bucket assignment is layout only, not frontend condition filtering.
    const cells = useMemo(() => {
        const map = new Map();
        for (const bucket of buckets)
            for (const brand of visibleBrands)
                map.set(`${bucket.id}:${brand.id}`, []);
        for (const product of productRows) {
            const bucket = buckets.find((value) => isInBucket(product.price, value));
            if (bucket) map.get(`${bucket.id}:${product.brandId}`)?.push(product);
        }
        return map;
    }, [productRows, buckets, visibleBrands.map((brand) => brand.id).join("|")]);
    const brandCounts = useMemo(() => {
        const counts = new Map();
        for (const product of productRows)
            counts.set(product.brandId, (counts.get(product.brandId) || 0) + 1);
        return counts;
    }, [productRows]);
    const ramOptions = filterOptions.ram;
    const groupOfRamRomOptions  = filterOptions.groupOfRamRom;
    const storageOptions = filterOptions.storage;
    const networkOptions = filterOptions.network;
    const defaultCountryId =
        countries.find((country) => country.code === "global")?.id || "";
    const hasFilters =
        filters.launchStart ||
        filters.launchEnd ||
        filters.countryId !== defaultCountryId ||
        filters.query ||
        filters.ram ||
        filters.storage ||
        filters.groupOfRamRom ||
        filters.network ||
        filters.brandIds.length !== brands.length;

    function changeFilter(key, value) {
        setFilters((prev) => ({ ...prev, [key]: value }));
    }
    function applyRange(e) {
        e?.preventDefault();
        if (Object.values(draft).some((v) => !v.trim())) {
            setRangeError("请填写完整的价格范围和间隔。");
            return;
        }
        const next = {
            min: Number(draft.min),
            max: Number(draft.max),
            step: Number(draft.step),
        };
        const error = validateRange(next);
        if (error) {
            setRangeError(error);
            return;
        }
        if (
            Object.values(next).some(
                (v) => Math.abs(v * 100 - Math.round(v * 100)) > 1e-7,
            )
        ) {
            setRangeError("价格和间隔最多支持两位小数。");
            return;
        }
        try {
            createBuckets(next);
        } catch (error) {
            setRangeError(error.message);
            return;
        }
        setRange(next);
        setRangeError("");
    }
    async function saveProductPrice(id, price) {
        const updated = await updateProductPrice(id, price);
        const view = {
            ...updated,
            country: countryMap.get(updated.countryId)?.name || "未指定",
        };
        setDetail((prev) => (prev?.id === id ? view : prev));
        // Query the backend again rather than removing/keeping rows through local filters.
        setProductRevision((value) => value + 1);
        setAnnouncement(`商品价格已更新为 ${price} 美元，商品数据已重新请求。`);
    }
    function resetFilters() {
        setFilters({
            query: "",
            brandIds: brands.map((b) => b.id),
            ram: "",
            storage: "",
            network: "",
            groupOfRamRom: "",
            countryId: defaultCountryId,
            ...getDefaultLaunchDates()
        });
    }
    function completeReorder(source, target) {
        setOrder((prev) => moveBrand(prev, source, target));
        setAnnouncement(
            `已将 ${brandMap.get(source)?.name} 移动到 ${brandMap.get(target)?.name} 的位置，顺序仅当前页面有效。`,
        );
    }
    function startDrag(e, id) {
        if (e.button !== 0) return;
        e.currentTarget.setPointerCapture(e.pointerId);
        dragRef.current = {
            source: id,
            target: id,
            startX: e.clientX,
            active: false,
        };
    }
    function moveDrag(e) {
        const state = dragRef.current;
        if (!state) return;
        if (!state.active && Math.abs(e.clientX - state.startX) < 5) return;
        state.active = true;
        const header = document
            .elementFromPoint(e.clientX, e.clientY)
            ?.closest("[data-brand-id]");
        if (header) state.target = header.dataset.brandId;
        setDrag({ ...state });
        // 指针靠近容器边缘时横向滚动，可将品牌拖到当前屏幕以外。
        const scroller = e.currentTarget.closest(".matrix-scroll");
        const rect = scroller.getBoundingClientRect();
        if (e.clientX > rect.right - 50) scroller.scrollLeft += 22;
        if (e.clientX < rect.left + 130) scroller.scrollLeft -= 22;
    }
    function endDrag() {
        const state = dragRef.current;
        if (state?.active && state.source !== state.target)
            completeReorder(state.source, state.target);
        dragRef.current = null;
        setDrag(null);
    }
    function keyboardReorder(e, id) {
        if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
        e.preventDefault();
        const ids = visibleBrands.map((b) => b.id);
        const target = ids[ids.indexOf(id) + (e.key === "ArrowRight" ? 1 : -1)];
        if (target) completeReorder(id, target);
    }

    return (
        <main className="app-shell" data-loading={loading ? "true" : "false"}>
            <header className="page-header">
                <div>
                    <div className="eyebrow">
                        <span className="mark">▦</span> MARKETING ROADMAP
                    </div>
                    <h1>
                        商品价格矩阵
                    </h1>
                </div>
            </header>

            <FilterPanel
                brands={brands}
                countries={countries}
                disabled={!lookupsReady}
                filters={filters}
                changeFilter={changeFilter}
                resetFilters={resetFilters}
                hasFilters={hasFilters}
                ramOptions={ramOptions}
                groupOfRamRomOptions={groupOfRamRomOptions}
                storageOptions={storageOptions}
                networkOptions={networkOptions}
                launchError={launchError}
                draft={draft}
                setDraft={setDraft}
                applyRange={applyRange}
                buckets={buckets}
                rangeError={rangeError}
            />

            <section className="matrix-panel" aria-label="商品价格矩阵">
                <div className="matrix-toolbar">
                    <div>
                        <strong>价格分布</strong>
                        <span className="count-badge" data-testid="product-count">
                            {productRows.length} 款商品 | 价格最高: ${maxPrice}, 最低: ${minPrice} | 暂缺价格商品：{noProducts} 个
                        </span>
                        <span className="brand-count">{visibleBrands.length} 个品牌</span>
                    </div>
                    <div className="toolbar-actions">
                        <span className="drag-hint">
                          <Icon name="grip" size={14} /> 拖动品牌调整顺序
                          <span className="desktop-note"> · 仅本次生效</span>
                        </span>
                        <div className="field-picker">
                            <button
                                className="field-config-button"
                                aria-expanded={fieldPanel}
                                aria-controls="card-field-options"
                                onClick={() => {
                                    setFieldPanel(!fieldPanel);
                                }}
                            >
                                <Icon name="filter" size={14} /> 卡片字段配置{" "}
                                <span className="chevron">⌄</span>
                            </button>
                            {fieldPanel && (
                                <>
                                    <button
                                        className="popover-backdrop"
                                        aria-label="关闭卡片字段配置"
                                        onClick={() => setFieldPanel(false)}
                                    />
                                    <div
                                        className="brand-popover field-popover"
                                        id="card-field-options"
                                        role="group"
                                        aria-label="卡片字段显示设置"
                                    >
                                        <div className="popover-top">
                                            <strong>选择显示字段</strong>
                                            <button
                                                onClick={() =>
                                                    setCardFields({ ...DEFAULT_CARD_FIELDS })
                                                }
                                            >
                                                恢复默认
                                            </button>
                                        </div>
                                        <label>
                                            <input type="checkbox" checked disabled />
                                            型号名称（固定）
                                        </label>
                                        {CARD_FIELD_OPTIONS.map(([key, label]) => (
                                            <label key={key}>
                                                <input
                                                    type="checkbox"
                                                    aria-label={`显示${label}`}
                                                    checked={cardFields[key]}
                                                    onChange={(e) =>
                                                        setCardFields((prev) => ({
                                                            ...prev,
                                                            [key]: e.target.checked,
                                                        }))
                                                    }
                                                />
                                                {label}
                                            </label>
                                        ))}
                                        <label className="remember-fields">
                                            <input
                                                type="checkbox"
                                                aria-label="记住字段配置"
                                                checked={rememberFields}
                                                onChange={(e) => setRememberFields(e.target.checked)}
                                            />
                                            记住字段配置
                                        </label>
                                        <p className="field-note">
                                            {storageError
                                                ? "浏览器存储不可用，配置仅当前页面生效。"
                                                : rememberFields
                                                    ? "已自动保存到当前浏览器，下次打开保留。"
                                                    : "仅当前页面生效，下次打开恢复默认。"}
                                            <br />
                                            不影响品牌顺序、筛选与详情。
                                        </p>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
                {loading ? (
                    <div className="state-panel" role="status">
                        <span className="loading-dot" /> 正在加载商品信息…
                    </div>
                ) : loadError ? (
                    <div className="state-panel" role="alert">
                        <h3>商品加载失败</h3>
                        <p>{loadError}</p>
                        <button className="primary" onClick={() => retryLoad()}>
                            重新加载
                        </button>
                    </div>
                ) : visibleBrands.length === 0 ? (
                    <div className="state-panel">
                        <h3>尚未选择品牌</h3>
                        <p>选择至少一个品牌来查看商品价格分布。</p>
                        <button
                            className="primary"
                            onClick={() =>
                                changeFilter(
                                    "brandIds",
                                    brands.map((b) => b.id),
                                )
                            }
                        >
                            展示全部品牌
                        </button>
                    </div>
                ) : (
                    <>
                        {productRows.length === 0 && (
                            <div className="empty-banner" role="status">
                                当前条件下没有匹配的商品，试试调整筛选或价格范围。
                                <button onClick={resetFilters}>清除筛选</button>
                            </div>
                        )}
                        <div className={`matrix-scroll ${drag ? "is-dragging" : ""}`}>
                            <table
                                className="matrix-table"
                                style={{ minWidth: `${108 + visibleBrands.length * 172}px` }}
                            >
                                <caption className="sr-only">
                                    按品牌和价格区间分类的商品信息，价格从高到低排列，区间左闭右开，最高段包含最高价格。
                                </caption>
                                <colgroup>
                                    <col style={{ width: "108px" }} />
                                    {visibleBrands.map((b) => (
                                        <col key={b.id} />
                                    ))}
                                </colgroup>
                                <thead>
                                <tr>
                                    <th className="axis-header" scope="col">
                                        价格 <span>USD ↓</span>
                                    </th>
                                    {visibleBrands.map((b) => (
                                        <th
                                            key={b.id}
                                            scope="col"
                                            data-brand-id={b.id}
                                            className={`brand-heading ${drag?.source === b.id ? "drag-source" : ""} ${drag?.target === b.id && drag.source !== b.id ? "drop-target" : ""}`}
                                        >
                                            <div className="brand-title">
                                                <BrandLabel brand={b} />
                                                <button
                                                    className="drag-handle"
                                                    aria-label={`拖动 ${b.name} 调整顺序`}
                                                    title="拖动调整顺序，也可聚焦后按左右方向键"
                                                    onPointerDown={(e) => startDrag(e, b.id)}
                                                    onPointerMove={moveDrag}
                                                    onPointerUp={endDrag}
                                                    onPointerCancel={() => {
                                                        dragRef.current = null;
                                                        setDrag(null);
                                                    }}
                                                    onKeyDown={(e) => keyboardReorder(e, b.id)}
                                                >
                                                    <Icon name="grip" size={18} />
                                                </button>
                                            </div>
                                            <span className="column-count">
                                                {brandCounts.get(b.id) || 0} 款商品
                                            </span>
                                        </th>
                                    ))}
                                </tr>
                                </thead>
                                <tbody>
                                {buckets.map((bucket) => (
                                    <tr key={bucket.id} data-bucket={bucket.id}>
                                        <th className="price-axis" scope="row">
                                            <strong>{formatPrice(bucket.upper)}</strong>
                                            <span>
                                                {formatPrice(bucket.lower)} –{" "}{formatPrice(bucket.upper)}
                                            </span>
                                            <span className="sr-only">
                                                  {bucket.includeUpper ? "含上下界" : "含下界，不含上界"}
                                            </span>
                                        </th>
                                        {visibleBrands.map((brand) => {
                                            const products = cells.get(`${bucket.id}:${brand.id}`) || [];
                                            return (
                                                <td
                                                    key={brand.id}
                                                    className={
                                                        drag?.source === brand.id ? "drag-column" : ""
                                                    }
                                                >
                                                    <div className="product-stack">
                                                        {products.map((product) => (
                                                            <div
                                                                key={product.id}
                                                                className="product-card"
                                                                style={{
                                                                    "--brand-color": getBrandColor(brand),
                                                                }}
                                                            >
                                                                <button
                                                                    className="product-card-open"
                                                                    onClick={() => setDetail(product)}
                                                                    aria-label={[
                                                                        brand.name,
                                                                        product.name,
                                                                        formatConfigurations(product, cardFields),
                                                                        cardFields.network && product.network,
                                                                        cardFields.price &&
                                                                        `${product.price}美元`,
                                                                        cardFields.country &&
                                                                        (product.country || "国家未指定"),
                                                                        cardFields.launchDate &&
                                                                        (product.launchDate ||
                                                                            "上市日期未指定"),
                                                                        "查看详情",
                                                                    ]
                                                                        .filter(Boolean)
                                                                        .join("，")}
                                                                />
                                                                <div className="card-top">
                                                                    <strong>{product.name}</strong>
                                                                    {cardFields.network && product.is_5g !== 0 && (
                                                                        <span
                                                                            className="network-tag"
                                                                            data-field="network"
                                                                        >
                                                                            5G
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                {
                                                                    cardFields.chipset && (
                                                                        <div className="card-config">
                                                                            <span data-field="chipset">
                                                                                {product.chipset}
                                                                            </span>
                                                                        </div>
                                                                    )
                                                                }
                                                                {(cardFields.ram || cardFields.storage) && (
                                                                    <div className="card-config">
                                                                        {getProductConfigurations(product).map(
                                                                            ([ram, rom], index) => (
                                                                                <React.Fragment key={`${ram}:${rom}`}>
                                                                                    {index > 0 && (
                                                                                        <span className="config-divider">{" "}|{" "}</span>
                                                                                    )}
                                                                                    <span className="config-pair">
                                                                                        {cardFields.ram && (
                                                                                            <span data-field="ram">
                                                                                              {ram}{!cardFields.storage && "GB"}
                                                                                            </span>
                                                                                        )}
                                                                                        {cardFields.ram && cardFields.storage && "+"}
                                                                                        {cardFields.storage && (
                                                                                            <span data-field="storage">
                                                                                                {rom}GB
                                                                                            </span>
                                                                                        )}
                                                                                    </span>
                                                                                </React.Fragment>
                                                                            ),
                                                                        )}
                                                                        {getProductConfigurations(product)
                                                                            .length === 0 && "配置未指定"}
                                                                    </div>
                                                                )}

                                                                {
                                                                    cardFields.camera && (
                                                                        <div className="card-config">
                                                                            <span data-field="camera">
                                                                                {product.camera?.map(item=>`${item.mp}`).join(" + ")}
                                                                            </span>
                                                                        </div>
                                                                    )
                                                                }

                                                                {
                                                                    cardFields.display && (
                                                                        <div className="card-config">
                                                                            <span data-field="display">
                                                                                {product.display?.size} | {product.display?.resolution}
                                                                            </span>
                                                                        </div>
                                                                    )
                                                                }

                                                                {
                                                                    cardFields.battery && (
                                                                        <div className="card-config">
                                                                            <span data-field="battery">
                                                                                {product.battery?.capacity_mah} mAh
                                                                                {product.battery?.wired_charging_power_w ? ` | ${product.battery?.wired_charging_power_w}W` : ''}
                                                                                {product.battery?.reverse_wired_charging_power_w ? ` + ${product.battery?.reverse_wired_charging_power_w}W` : ''}
                                                                            </span>
                                                                        </div>
                                                                    )
                                                                }

                                                                {cardFields.country && (
                                                                    <div
                                                                        className="card-extra"
                                                                        data-field="country"
                                                                    >
                                                                        国家：{product.country || "未指定"}
                                                                    </div>
                                                                )}
                                                                {cardFields.launchDate && (
                                                                    <div
                                                                        className="card-extra"
                                                                        data-field="launchDate"
                                                                    >
                                                                        上市：{product.launchDate || "未指定"}
                                                                    </div>
                                                                )}
                                                                <div className="card-bottom">
                                                                    {cardFields.price && (
                                                                        <span className="card-price-actions">
                                                                            <span
                                                                                className="card-price"
                                                                                data-field="price"
                                                                            >
                                                                              {product.currencyCode} {formatPrice(product.price)}
                                                                            </span>
                                                                            {/*
                                                                            <button
                                                                                className="card-price-edit"
                                                                                type="button"
                                                                                aria-label={`编辑 ${product.name} 的价格`}
                                                                                title="编辑价格"
                                                                                onClick={() =>
                                                                                    setPriceEditor(product)
                                                                                }
                                                                            >
                                                                              <EditIcon />
                                                                            </button>
                                                                            */}
                                                                        </span>
                                                                    )}
                                                                    <span className="card-detail">详情 <Icon name="arrow" size={11} /></span>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                                </tbody>
                                <tfoot>
                                <tr>
                                    <th className="axis-bottom">{formatPrice(range.min)}</th>
                                    <td colSpan={visibleBrands.length} />
                                </tr>
                                </tfoot>
                            </table>
                        </div>
                    </>
                )}
                <footer className="matrix-footer">
          <span>
            <span className="legend-dot" />{" "}
              同一品牌、同一价格段内，商品按价格从高到低排列
          </span>
                    <span>区间左闭右开，最高段含上限</span>
                </footer>
            </section>
            <footer className="page-footer">
        <span>
          MARKETING ROADMAP <span className="footer-separator">/</span>{" "}
            © {new Date().getFullYear()}
        </span>
                <span>数据来源 · GSM资料</span>
            </footer>
            <div className="sr-only" aria-live="polite">
                {announcement}
            </div>
            {priceEditor && (
                <PriceEditor
                    product={priceEditor}
                    brand={brandMap.get(priceEditor.brandId)}
                    onClose={() => setPriceEditor(null)}
                    onSavePrice={saveProductPrice}
                />
            )}
            {detail && (
                <ProductDetails
                    product={detail}
                    brand={brandMap.get(detail.brandId)}
                    onClose={() => setDetail(null)}
                    onSavePrice={saveProductPrice}
                />
            )}
        </main>
    );
}

createRoot(document.getElementById("root")).render(
    <React.StrictMode>
        <App />
    </React.StrictMode>,
);
